import { Button, Select, DatePicker, Switch, Input, Tooltip } from "antd";
import {
  CalendarIcon,
  DoubleArrow,
  DownloadIcon,
  FilterIcon,
  SearchIcon,
  SelectArrow,
  TickIcon,
} from "../../../../atoms/CustomIcons";
import { MinusOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { getClientManagerFormList } from "../../ducks/actions";
import { useAppDispatch, useAppSelector } from "../../../../config/hooks";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

interface FilterProps {
  setFilters: React.Dispatch<
    React.SetStateAction<{
      client_manager: undefined | string[];
      status: undefined | string[];
      company_size: undefined | string[];
      state: undefined;
      city: undefined;
      onboarding_complete_date: string[] | undefined;
      last_user_activity: string[] | undefined;
      search_text: undefined | string;
    }>
  >;
  filters: {
    client_manager: undefined | string[];
    status: undefined | string[];
    company_size: undefined | string[];
    state: undefined;
    city: undefined;
    onboarding_complete_date: string[] | undefined;
    last_user_activity: string[] | undefined;
    search_text: undefined | string;
  };
  archived: boolean;
  isArchived: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Filters(props: FilterProps) {
  const dispatch = useAppDispatch();
  const { setFilters, filters, archived, isArchived } = props;
  const [showFilters, setShowFilters] = useState(false);
  const clientManagerList = useAppSelector(
    (state) => state?.company?.companyClientManager
  );

  const onChange = (checked: boolean) => {
    isArchived(checked);
    if (checked) {
      setFilters((prev) => ({
        ...prev,
        status: ["archived"],
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        status: undefined,
      }));
    }
  };

  const clearFilters = () => {
    setFilters({
      client_manager: undefined,
      status: undefined,
      company_size: undefined,
      state: undefined,
      city: undefined,
      onboarding_complete_date: undefined,
      last_user_activity: undefined,
      search_text: undefined,
    });
    isArchived(false);
  };

  useEffect(() => {
    dispatch(getClientManagerFormList());
  }, [dispatch]);

  const states = [
    { label: "CA", value: "CA" },
    { label: "TX", value: "TX" },
    { label: "NY", value: "NY" },
    { label: "FL", value: "FL" },
    { label: "IL", value: "IL" },
    { label: "PA", value: "PA" },
  ];

  const cities = [
    { label: "New York", value: "New York" },
    { label: "Los Angeles", value: "Los Angeles" },
    { label: "Chicago", value: "Chicago" },
    { label: "Houston", value: "Houston" },
    { label: "Phoenix", value: "Phoenix" },
    { label: "Philadelphia", value: "Philadelphia" },
    { label: "San Antonio", value: "San Antonio" },
    { label: "San Diego", value: "San Diego" },
    { label: "Dallas", value: "Dallas" },
    { label: "San Jose", value: "San Jose" },
  ];

  return (
    <div className="filtersWrapper">
      <div className="filtersTop">
        <div className="filterSearch">
          <Input
            prefix={<SearchIcon />}
            placeholder="Search companies"
            value={filters?.search_text}
            allowClear
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                search_text: e.target.value,
              }))
            }
          />
        </div>
        <div className="filterToggle">
          <Button
            icon={<FilterIcon />}
            className={`outlined-btn ${showFilters && "show_filters"} ${
              archived && "archived"
            }`}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters{" "}
            <div className="filter_icon">
              <SelectArrow />
            </div>
          </Button>
        </div>
        <div className="filterDownload">
          <Button icon={<DownloadIcon />} />
        </div>
      </div>
      {showFilters && (
        <div className="filtersBottom">
          <div className="btmLeft">
            <div className="filterBox">
              <Tooltip title="Client manager" mouseEnterDelay={0.4}>
                <Select
                  style={{ width: "250px" }}
                  suffixIcon={<SelectArrow />}
                  menuItemSelectedIcon={
                    <TickIcon style={{ color: "#00A07D" }} />
                  }
                  showSearch
                  allowClear
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, client_manager: value }))
                  }
                  value={filters?.client_manager}
                  placeholder="Client manager"
                  options={clientManagerList}
                  mode="multiple"
                  maxTagCount={1}
                />
              </Tooltip>
            </div>

            <div className="filterBox">
              <Tooltip title="Account status" mouseEnterDelay={0.4}>
                <Select
                  style={{ width: "250px" }}
                  suffixIcon={<SelectArrow />}
                  menuItemSelectedIcon={
                    <TickIcon style={{ color: "#00A07D" }} />
                  }
                  showSearch
                  allowClear
                  placeholder="Account status"
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, status: value }))
                  }
                  value={filters?.status}
                  options={[
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                    { value: "onboarding", label: "Onboarding" },
                    { value: "suspended", label: "Suspended" },
                  ]}
                  mode="multiple"
                  maxTagCount={1}
                />
              </Tooltip>
            </div>

            <div className="filterBox">
              <Tooltip title="Company size" mouseEnterDelay={0.4}>
                <Select
                  style={{ width: "250px" }}
                  suffixIcon={<SelectArrow />}
                  menuItemSelectedIcon={
                    <TickIcon style={{ color: "#00A07D" }} />
                  }
                  showSearch
                  allowClear
                  placeholder="Company size"
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, company_size: value }))
                  }
                  value={filters?.company_size}
                  options={[
                    { value: "small", label: "Small" },
                    { value: "medium", label: "Medium" },
                    { value: "large", label: "Large" },
                    { value: "enterprise", label: "Enterprise" },
                  ]}
                  mode="multiple"
                  maxTagCount={1}
                />
              </Tooltip>
            </div>

            <div className="filterBox">
              <Tooltip title="State" mouseEnterDelay={0.4}>
                <Select
                  style={{ width: "250px" }}
                  suffixIcon={<SelectArrow />}
                  menuItemSelectedIcon={
                    <TickIcon style={{ color: "#00A07D" }} />
                  }
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, state: value }))
                  }
                  value={filters?.state}
                  showSearch
                  allowClear
                  placeholder="State"
                  options={states}
                />
              </Tooltip>
            </div>

            <div className="filterBox">
              <Tooltip title="City" mouseEnterDelay={0.4}>
                <Select
                  style={{ width: "250px" }}
                  suffixIcon={<SelectArrow />}
                  menuItemSelectedIcon={
                    <TickIcon style={{ color: "#00A07D" }} />
                  }
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, city: value }))
                  }
                  value={filters?.city}
                  showSearch
                  allowClear
                  placeholder="City"
                  options={cities}
                />
              </Tooltip>
            </div>

            <div className="filterBox">
              <Tooltip title="Onboarding completed dates" mouseEnterDelay={0.4}>
                <RangePicker
                  suffixIcon={<CalendarIcon />}
                  separator={<MinusOutlined />}
                  superNextIcon={<DoubleArrow />}
                  superPrevIcon={<DoubleArrow />}
                  prevIcon={<SelectArrow />}
                  nextIcon={<SelectArrow />}
                  value={
                    filters?.onboarding_complete_date &&
                    filters?.onboarding_complete_date?.[0] !== "" &&
                    filters?.onboarding_complete_date?.[1] !== ""
                      ? [
                          dayjs(filters.onboarding_complete_date?.[0]),
                          dayjs(filters.onboarding_complete_date?.[1]),
                        ]
                      : undefined
                  }
                  onChange={(_date, dateString) =>
                    setFilters((prev) => ({
                      ...prev,
                      onboarding_complete_date: dateString,
                    }))
                  }
                  placeholder={["MM/DD/YYYY", "MM/DD/YYYY"]}
                  format={[
                    "MM/DD/YYYY",
                    "YYYY-MM-DD",
                    "MMM D, YYYY",
                    "MM-DD-YYYY",
                    "MMDDYYYY",
                    "DDMMYYYY",
                    "DD/MM/YYYY",
                    "DD-MM-YYYY",
                    "MMM D YYYY",
                    "M-D-YYYY",
                    "M/D/YYYY",
                    "D-M-YYYY",
                    "D/M/YYYY",
                  ]}
                />
              </Tooltip>
            </div>

            <div className="filterBox">
              <Tooltip title="Last user activity" mouseEnterDelay={0.4}>
                <RangePicker
                  suffixIcon={<CalendarIcon />}
                  separator={<MinusOutlined />}
                  superNextIcon={<DoubleArrow />}
                  superPrevIcon={<DoubleArrow />}
                  prevIcon={<SelectArrow />}
                  nextIcon={<SelectArrow />}
                  value={
                    filters?.last_user_activity &&
                    filters?.last_user_activity?.[0] !== "" &&
                    filters?.last_user_activity?.[1] !== ""
                      ? [
                          dayjs(filters.last_user_activity?.[0]),
                          dayjs(filters.last_user_activity?.[1]),
                        ]
                      : undefined
                  }
                  onChange={(_date, dateString) =>
                    setFilters((prev) => ({
                      ...prev,
                      last_user_activity: dateString,
                    }))
                  }
                  placeholder={["MM/DD/YYYY", "MM/DD/YYYY"]}
                  format={[
                    "MM/DD/YYYY",
                    "YYYY-MM-DD",
                    "MMM D, YYYY",
                    "MM-DD-YYYY",
                    "MMDDYYYY",
                    "DDMMYYYY",
                    "DD/MM/YYYY",
                    "DD-MM-YYYY",
                    "MMM D YYYY",
                    "M-D-YYYY",
                    "M/D/YYYY",
                    "D-M-YYYY",
                    "D/M/YYYY",
                  ]}
                />
              </Tooltip>
            </div>

            {/* <div className="filterBox">
              <Tooltip title="Last user activity dates" mouseEnterDelay={0.4}>
                <RangePicker
                  suffixIcon={<CalendarIcon />}
                  separator={<MinusOutlined />}
                  superNextIcon={<DoubleArrow />}
                  superPrevIcon={<DoubleArrow />}
                  prevIcon={<SelectArrow />}
                  nextIcon={<SelectArrow />}
                  value={
                    filters?.last_user_activity &&
                    filters?.last_user_activity?.[0] !== "" &&
                    filters?.last_user_activity?.[1] !== ""
                      ? [
                          dayjs(filters.last_user_activity?.[0]),
                          dayjs(filters.last_user_activity?.[1]),
                        ]
                      : undefined
                  }
                  onChange={(_date, dateString) =>
                    setFilters((prev) => ({
                      ...prev,
                      last_user_activity: dateString,
                    }))
                  }
                  placeholder={["MMM D, YYYY", "MMM D, YYYY"]}
                  format={[
                    "YYYY-MM-DD",
                    "MMM D, YYYY",
                    "MM/DD/YYYY",
                    "MM-DD-YYYY",
                    "MMDDYYYY",
                    "DDMMYYYY",
                    "DD/MM/YYYY",
                    "DD-MM-YYYY",
                    "MMM D YYYY",
                    "M-D-YYYY",
                    "M/D/YYYY",
                    "D-M-YYYY",
                    "D/M/YYYY",
                  ]}
                />
              </Tooltip>
            </div> */}

            <div className="filterBox">
              <div className="switchBox">
                <Switch onChange={onChange} checked={archived} />
                {archived ? "Hide archived" : "Show archived"}
              </div>
            </div>
          </div>
          <div className="btmRight">
            <Button
              className="outlined-btn"
              onClick={clearFilters}
              disabled={
                !filters.search_text &&
                !filters.status &&
                !filters.client_manager &&
                !filters.status &&
                !filters.company_size &&
                !filters.onboarding_complete_date &&
                !filters.search_text &&
                !filters.state &&
                !filters.city &&
                !archived
              }
            >
              Clear filters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
